import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedList } from 'react-intl';
import styled from 'styled-components';

import { InGameCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { EssenceRewardCount } from '@sorare/core/src/components/rewards/RewardCount';
import { getName } from '@sorare/core/src/lib/cards';
import { cardPull } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import type {
  Rewards_anyRewardInterface,
  Rewards_anyRewardInterface_CardShardsReward_,
} from './__generated__/index.graphql.ts';

const Root = styled.span`
  margin-top: var(--half-unit);
  display: block;
`;

const clueMessages = {
  [InGameCurrency.COMPETITION_CRAFT_CLUE]: cardPull.competitionClue,
  [InGameCurrency.COUNTRY_CRAFT_CLUE]: cardPull.countryClue,
  [InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE]: cardPull.fiftyFiftyClue,
};

type Props = {
  rewards: Nullable<Rewards_anyRewardInterface[]>;
};

export const Rewards = ({ rewards }: Props) => {
  const cardRewards =
    rewards
      ?.map(r => isType(r, 'AnyCardReward') && getName(r.card))
      .filter(Boolean) || [];

  const rewardBoxRewards =
    rewards
      ?.map(
        r =>
          isType(r, 'ProbabilisticBundleReward') &&
          r.probabilisticBundle.config.name
      )
      .filter(Boolean) || [];

  const mergedEssenceRewards =
    rewards
      ?.filter(r => isType(r, 'CardShardsReward'))
      .filter(Boolean)
      .reduce<Rewards_anyRewardInterface_CardShardsReward_[]>((acc, r) => {
        const scarcityIndex = acc.findIndex(e => e.rarity === r.rarity);
        if (scarcityIndex === -1) {
          acc.push(r);
        } else {
          acc[scarcityIndex] = {
            ...acc[scarcityIndex],
            quantity: acc[scarcityIndex].quantity + r.quantity,
          };
        }
        return acc;
      }, [])
      .map(r => (
        <EssenceRewardCount
          key={`${r.rarity}-${r.quantity}`}
          quantity={r.quantity}
          rarity={r.rarity}
        />
      )) || [];

  const inGameCurrencyRewards =
    rewards
      ?.filter(r => isType(r, 'InGameCurrencyReward'))
      ?.map(r => {
        switch (r.config.currency) {
          case InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE:
          case InGameCurrency.COUNTRY_CRAFT_CLUE:
          case InGameCurrency.COMPETITION_CRAFT_CLUE:
            return (
              <FormattedMessage
                key={r.id}
                defaultMessage="{amount} crafting clue: {craftingClueType}"
                values={{
                  amount: r.config.amount,
                  craftingClueType: (
                    <FormattedMessage {...clueMessages[r.config.currency]} />
                  ),
                }}
              />
            );
          case InGameCurrency.WHEEL_TICKET:
            return (
              <FormattedMessage
                key={r.id}
                defaultMessage="x {amount} {amount, plural, one {wheel ticket} other {wheel tickets}}"
                values={{
                  amount: r.config.amount,
                }}
              />
            );
          default:
            return null;
        }
      })
      .filter(Boolean) || [];

  const rewardsText = [
    ...cardRewards,
    ...rewardBoxRewards,
    ...mergedEssenceRewards,
    ...inGameCurrencyRewards,
  ];

  if (!rewardsText.length) return null;
  return (
    <Root>
      <FormattedMessage
        defaultMessage="<b>Special Offer:</b> you also won {rewardsText}"
        values={{
          rewardsText: <FormattedList type="conjunction" value={rewardsText} />,
          b: Bold,
        }}
      />
    </Root>
  );
};

Rewards.fragments = {
  anyReward: gql`
    fragment Rewards_anyRewardInterface on AnyRewardInterface {
      id
      ... on AnyCardReward {
        id
        card {
          slug
          ...getName_anyCard
        }
      }
      ... on CardShardsReward {
        id
        quantity
        rarity
      }
      ... on ProbabilisticBundleReward {
        id
        probabilisticBundle {
          id
          config {
            id
            name
          }
        }
      }
      ... on InGameCurrencyReward {
        id
        config {
          id
          amount
          currency
        }
      }
    }
    ${getName.fragments.anyCard}
  ` as TypedDocumentNode<Rewards_anyRewardInterface>,
};
